import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoCheckmarkCircle,
  IoCallOutline,
  IoLocationOutline,
  IoCubeOutline,
  IoCalendarOutline,
  IoTimeOutline,
} from 'react-icons/io5';
import { SERVICE_LABELS } from '../../services/customerFormService';

const CONTACT_PHONE = '070-7666-9413';

const buildAppNumber = (id) => {
  const now = new Date();
  const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  let suffix = '';
  if (id) {
    suffix = id.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
  }
  if (suffix.length < 4) {
    suffix = (suffix + Math.random().toString(36).slice(2, 6).toUpperCase()).slice(0, 4);
  }
  return `APP-${ymd}-${suffix}`;
};

export default function ApplySuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const state = location.state || {};
  const queryServices = (searchParams.get('services') || '').split(',').filter(Boolean);
  const services = state.services && state.services.length > 0 ? state.services : queryServices;
  const customerName = state.name || searchParams.get('name') || '';
  const phone = state.phone || searchParams.get('phone') || '';
  const movingAddress = state.moving_address || searchParams.get('address') || '';
  const areaPyeong = state.area_pyeong_exact || searchParams.get('area') || '';
  const movingDate = state.moving_date || searchParams.get('moving_date') || '';

  const appNumber = useMemo(
    () => buildAppNumber(state.requestId || state.customerId || ''),
    [state.requestId, state.customerId],
  );

  const formatPhoneDisplay = (p) => {
    const d = (p || '').replace(/\D/g, '');
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return p || '';
  };

  return (
    <PageOuter>
      <Page>
        <Header>
          <button type="button" onClick={() => navigate('/')}>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 1L1 8l8 7" />
            </svg>
            돌아가기
          </button>
        </Header>

        <Container>
          <Card>
            <IconCircle>
              <IoCheckmarkCircle size={56} />
            </IconCircle>
            <Title>신청이 완료되었어요!</Title>
            <SubTitle>접수번호로 진행 상태를 확인할 수 있습니다.</SubTitle>

            <AppPill>{appNumber}</AppPill>

            {services.length > 0 && (
              <SummaryBox>
                <SummaryHead>
                  <IoCubeOutline size={14} />
                  <span>신청 서비스</span>
                </SummaryHead>
                <ServiceList>
                  {services.map((s) => (
                    <ServiceChip key={s}>{SERVICE_LABELS[s] || s}</ServiceChip>
                  ))}
                </ServiceList>

                {(movingAddress || areaPyeong || movingDate) && (
                  <DetailLines>
                    {movingAddress && (
                      <DetailRow>
                        <IoLocationOutline size={14} />
                        <span>{movingAddress}</span>
                      </DetailRow>
                    )}
                    {areaPyeong && (
                      <DetailRow>
                        <IoCubeOutline size={14} />
                        <span>{areaPyeong}평</span>
                      </DetailRow>
                    )}
                    {movingDate && (
                      <DetailRow>
                        <IoCalendarOutline size={14} />
                        <span>이사일 {movingDate}</span>
                      </DetailRow>
                    )}
                  </DetailLines>
                )}
              </SummaryBox>
            )}

            {(customerName || phone) && (
              <ContactBox>
                <ContactLabel>
                  <IoCallOutline size={14} />
                  <span>연락처 정보</span>
                </ContactLabel>
                {customerName && <ContactRow><strong>{customerName}</strong></ContactRow>}
                {phone && <ContactRow>{formatPhoneDisplay(phone)}</ContactRow>}
              </ContactBox>
            )}

            <NoticeBox>
              <IoTimeOutline size={16} />
              <span>1-2 영업일 내에 알려드릴게요.</span>
            </NoticeBox>

            <CtaRow>
              <PrimaryBtn type="button" onClick={() => navigate('/apply/my')}>
                신청 내역 확인
              </PrimaryBtn>
              <OutlineBtn type="button" onClick={() => navigate('/apply')}>
                추가 신청하기
              </OutlineBtn>
            </CtaRow>

            <ContactFooter>
              문의 {CONTACT_PHONE}
            </ContactFooter>
          </Card>
        </Container>
      </Page>
    </PageOuter>
  );
}

const PageOuter = styled.div`
  min-height: 100vh;
  background: #E5E7EB;
  display: flex;
  justify-content: center;
`;

const Page = styled.div`
  width: 100%;
  max-width: 480px;
  min-height: 100vh;
  background: #F8FAFC;
  box-shadow: 0 0 20px rgba(0,0,0,0.08);
`;

const Header = styled.div`
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  padding: 18px 20px;

  button {
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: 0;
    font-size: 15px;
    font-weight: 400;
    color: #888;
    cursor: pointer;
    padding: 0;
  }
`;

const Container = styled.div`
  padding: 24px 20px 40px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 32px 22px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: #EEF2FF;
  color: #4A6CF7;
  margin-bottom: 18px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 800;
  color: #1F2937;
  margin: 0 0 6px;
`;

const SubTitle = styled.p`
  font-size: 13px;
  color: #6B7280;
  margin: 0 0 16px;
`;

const AppPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 999px;
  background: #4A6CF7;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  margin-bottom: 20px;
`;

const SummaryBox = styled.div`
  width: 100%;
  background: #F8FAFC;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
  text-align: left;
`;

const SummaryHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 10px;
`;

const ServiceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const ServiceChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: 999px;
  background: #EEF2FF;
  color: #4A6CF7;
  font-size: 12px;
  font-weight: 700;
`;

const DetailLines = styled.div`
  border-top: 1px dashed #E5E7EB;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4B5563;
`;

const ContactBox = styled.div`
  width: 100%;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  text-align: left;
`;

const ContactLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #6B7280;
  margin-bottom: 6px;
`;

const ContactRow = styled.div`
  font-size: 14px;
  color: #1F2937;

  & + & { margin-top: 2px; }
`;

const NoticeBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: #FEF9C3;
  color: #92400E;
  font-size: 13px;
  font-weight: 600;
  border-radius: 12px;
  margin-bottom: 18px;
`;

const CtaRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  background: #4A6CF7;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #3451D1; }
`;

const OutlineBtn = styled.button`
  min-height: 48px;
  border: 1.5px solid #4A6CF7;
  border-radius: 12px;
  background: #fff;
  color: #4A6CF7;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: #EEF2FF; }
`;

const ContactFooter = styled.p`
  margin-top: 16px;
  font-size: 12px;
  color: #9CA3AF;
`;
